import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { List, MapPlus } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { DetailPageLayout } from '@/components/layouts';
import { ServiceAreaMapEditor } from '@/components/service-areas/ServiceAreaMapEditor';
import { CoverageAreaList } from '@/components/service-areas/CoverageAreaList';
import { PlaceSearchBox } from '@/components/service-areas/PlaceSearchBox';
import { Fullbleed } from '@/contexts/fullbleed';

export const Route = createFileRoute('/_dashboard/dashboard/settings/service-areas')({
  component: ServiceAreasPage,
});

// The page has two surfaces: "My Coverage" (the grouped, named view of what the
// tenant serves) and "Add Coverage" (the map editor — add whole cities/counties
// or draw an area). ZIP codes stay the storage unit under the hood, but the
// operator never works with raw ZIPs here.
type ServiceAreaTab = 'coverage' | 'add';

function ServiceAreasPage() {
  const [tab, setTab] = useState<ServiceAreaTab>('coverage');

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold">Service Areas</h1>
            <p className="text-xs text-muted-foreground">
              The cities and areas you serve — used for tax resolution and scheduling.
            </p>
          </div>
        }
        contentClassName="flex min-h-0 flex-col p-0"
      >
        <Tabs
          value={tab}
          onValueChange={(value) => setTab(value as ServiceAreaTab)}
          className="min-h-0 flex-1"
        >
          <TabsList
            variant="line"
            className="h-auto w-full justify-start rounded-none border-b px-5 pt-3 pb-0"
          >
            <TabsTrigger value="coverage">
              <List className="size-3.5" />
              My Coverage
            </TabsTrigger>
            <TabsTrigger value="add">
              <MapPlus className="size-3.5" />
              Add Coverage
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="coverage"
            className="min-h-0 flex-1 data-[state=active]:flex data-[state=active]:flex-col data-[state=inactive]:hidden"
          >
            <CoverageAreaList />
          </TabsContent>

          <TabsContent
            value="add"
            className="min-h-0 flex-1 overflow-y-auto p-5 data-[state=inactive]:hidden"
          >
            <div className="mb-4">
              <PlaceSearchBox />
            </div>
            <div className="h-full min-h-[520px]">
              <ServiceAreaMapEditor />
            </div>
          </TabsContent>
        </Tabs>
      </DetailPageLayout>
    </>
  );
}
